import {Cell} from 'notebook/tables/constants/CellConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {PotentialCellErrors} from 'notebook/tables/errors/TableErrorConstants';
import * as Util from 'notebook/tables/excess/Util';

import {UnknownStructuredTable} from '../StructuredTableConstants';

export const SINGLE_INPUT_UNSUBMITTED_CHANGES_MESSAGE =
  'This value was updated for one or more cells after the table was last submitted. Convert single input into column to view individual values.';

export const SINGLE_INPUT_OUTDATED_MESSAGE =
  'This value is outdated for one or more cells. Convert single input into column to view individual values.';

export const MERGED_CELL_UNSUBMITTED_CHANGES_MESSAGE =
  'This value was updated for one or more merged cells after the table was last submitted. Unmerge the cell to view individual values.';

export const MERGED_CELL_OUTDATED_MESSAGE =
  'This value is outdated for one or more merged cells. Unmerge the cell to view individual values.';

/**
 * A condition that belongs to a hidden cell of a merge and therefore has to be reported on the merge's
 * visible (top-left) cell.
 *
 * This is a flat fact record — no JSX, no closures — matching the `unsubmittedMessage` / `staleMessage`
 * fields of the `CellHydration` records proposed in docs/notebook/structured-table-hydration-selectors-plan.md,
 * so that it can be folded into per-cell hydration facts rather than being applied as its own pass.
 */
export interface MergedCellBubbledCondition {
  rowIndex: number;
  colIndex: number;
  unsubmittedMessage?: string;
  staleMessage?: string;
}

/**
 * Find the unsubmitted and out of date conditions that need to be reported on a merged cell because
 * they belong to one of the cells it hides.
 *
 * Only the top-left cell of a merge is rendered — every covered cell produces no DOM at all — so a
 * condition on a hidden cell (e.g. a row whose entity value diverged from the value the merge supplies)
 * is otherwise invisible. This applies to any merge: single inputs, manual "merge cells", and pasted
 * merges alike. Reporting it on the merge's visible cell gives the user a dog ear telling them to unmerge
 * (or, for a single input, convert it into a column) to find the affected cells.
 *
 * Returns an empty array when the table has no merged cells, and only inspects the covered cells of the
 * merges it finds, so tables without merges do no work beyond the single scan for merges.
 *
 * @param structuredTable the structured table, read for its columns, merge spans, and row finalization
 * @param cellUnsubmittedStatuses per-cell unsubmitted status, as computed for the unsubmitted indicators
 * @param staleCellIndices the cells reported as out of date by the staleness indicators
 */
export function computeMergedCellBubbledConditions({
  structuredTable,
  cellUnsubmittedStatuses,
  staleCellIndices,
}: {
  structuredTable: UnknownStructuredTable;
  cellUnsubmittedStatuses: Array<Array<boolean>>;
  staleCellIndices: Array<{rowIndex: number; colIndex: number}>;
}): Array<MergedCellBubbledCondition> {
  const staleCellKeys = new Set(staleCellIndices.map(({rowIndex, colIndex}) => `${rowIndex},${colIndex}`));
  const conditions: Array<MergedCellBubbledCondition> = [];

  structuredTable.rows.forEach((row, rowIndex) => {
    row.cells.forEach((cell, colIndex) => {
      const rowSpan = cell?.mergeRowSpan ?? 1;
      const colSpan = cell?.mergeColSpan ?? 1;
      if (rowSpan === 1 && colSpan === 1) {
        return;
      }

      const isSingleInput = Boolean(structuredTable.columns[colIndex]?.isSingleInput);

      let hasHiddenUnsubmittedChanges = false;
      let hasHiddenOutdatedValue = false;
      for (let i = rowIndex; i < rowIndex + rowSpan; i++) {
        for (let j = colIndex; j < colIndex + colSpan; j++) {
          if (i === rowIndex && j === colIndex) {
            continue;
          }
          const hiddenRow = structuredTable.rows[i];
          // Mirrors the condition under which an unsubmitted indicator is added to a cell: an
          // unfinalized row is already reported as unsubmitted at the row level.
          if (
            hiddenRow?.isFinalized &&
            hiddenRow.lastSyncedUpstreamValues &&
            cellUnsubmittedStatuses[i]?.[j]
          ) {
            hasHiddenUnsubmittedChanges = true;
          }
          if (staleCellKeys.has(`${i},${j}`)) {
            hasHiddenOutdatedValue = true;
          }
        }
      }

      if (hasHiddenUnsubmittedChanges || hasHiddenOutdatedValue) {
        conditions.push({
          rowIndex,
          colIndex,
          ...(hasHiddenUnsubmittedChanges
            ? {
                unsubmittedMessage: isSingleInput
                  ? SINGLE_INPUT_UNSUBMITTED_CHANGES_MESSAGE
                  : MERGED_CELL_UNSUBMITTED_CHANGES_MESSAGE,
              }
            : {}),
          ...(hasHiddenOutdatedValue
            ? {staleMessage: isSingleInput ? SINGLE_INPUT_OUTDATED_MESSAGE : MERGED_CELL_OUTDATED_MESSAGE}
            : {}),
        });
      }
    });
  });

  return conditions;
}

/**
 * Report the given bubbled conditions as errors on the merged cells they belong to.
 *
 * Reuses the table, and every row array and cell that has no condition, so this only allocates for the
 * merged cells that actually gained one. A condition already present on the merge's own cell wins: the
 * specific message about that cell's own value is more useful than the generic "one or more cells"
 * message.
 *
 * This is the materialization half, and is the part that goes away once cell conditions are rendered
 * from hydration facts rather than written into cells.
 */
export function applyMergedCellBubbledConditions(
  table: Table,
  conditions: Array<MergedCellBubbledCondition>
): Table {
  if (conditions.length === 0) {
    return table;
  }

  const updatedRows = new Map<number, Array<Cell>>();
  for (const {rowIndex, colIndex, unsubmittedMessage, staleMessage} of conditions) {
    const cell = table.table[rowIndex]?.[colIndex];
    if (cell == null) {
      continue;
    }
    let errors: PotentialCellErrors = cell.error ?? null;
    if (unsubmittedMessage != null && !Util.hasErrorType(errors, Util.UnsubmittedChangesError.type)) {
      errors = Util.addError(errors, new Util.UnsubmittedChangesError(unsubmittedMessage));
    }
    if (staleMessage != null && !Util.hasErrorType(errors, Util.OutdatedError.type)) {
      errors = Util.addError(errors, new Util.OutdatedError(staleMessage));
    }
    if (errors === (cell.error ?? null)) {
      continue;
    }
    const updatedRow = updatedRows.get(rowIndex) ?? [...table.table[rowIndex]];
    updatedRow[colIndex] = {...cell, error: errors ?? undefined};
    updatedRows.set(rowIndex, updatedRow);
  }

  if (updatedRows.size === 0) {
    return table;
  }
  return {
    ...table,
    table: table.table.map((row, rowIndex) => updatedRows.get(rowIndex) ?? row),
  };
}
